/**
 * /admin/awards — manage award cycles, categories, moderate nominees, publish winners.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Award, Check, Clock, Edit2, FileSearch, Plus, Trash2, Trophy, Upload, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

const STATUS_OPTIONS = ['draft', 'nominations_open', 'nominations_closed', 'voting_open', 'voting_closed', 'published'];
const STATUS_LABEL = {
  draft: 'Draft',
  nominations_open: 'Nominations open',
  nominations_closed: 'Nominations closed',
  voting_open: 'Voting open',
  voting_closed: 'Voting closed',
  published: 'Published',
};
const STATUS_COLOUR = {
  draft: 'bg-stone text-charcoal',
  nominations_open: 'bg-mustard text-charcoal',
  nominations_closed: 'bg-stone text-charcoal',
  voting_open: 'bg-forest text-base',
  voting_closed: 'bg-charcoal text-base',
  published: 'bg-heritage text-base',
};

function NomineeEmailQueue() {
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/admin/awards/nominee-emails?status=draft').then((r) => setDrafts(r.data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('nominee-email-queued', h);
    return () => window.removeEventListener('nominee-email-queued', h);
  }, []);
  const patch = async (id, field, value) => {
    try { await api.patch(`/admin/awards/nominee-emails/${id}`, { [field]: value }); } catch (e) { /* noop */ }
  };
  const send = async (d) => {
    if (!window.confirm(`Send this email to ${d.to_email || '(no recipient set)'}?`)) return;
    setBusy(d.id);
    try {
      await api.post(`/admin/awards/nominee-emails/${d.id}/send`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
    setBusy('');
  };
  const dismiss = async (id) => {
    setBusy(id);
    try { await api.delete(`/admin/awards/nominee-emails/${id}`); await load(); } catch (e) { /* noop */ }
    setBusy('');
  };
  if (drafts.length === 0) return null;
  return (
    <div className="bg-white border border-mustard/60 rounded-3xl p-6 mb-8" data-testid="nominee-email-queue">
      <h3 className="font-display text-2xl mb-1">"You've been nominated" emails</h3>
      <div className="text-xs text-muted mb-4">Awaiting your approval — nothing is sent automatically. Check (or add) the recipient address, tweak the wording if needed, then approve.</div>
      <div className="space-y-4">
        {drafts.map((d) => (
          <div key={d.id} className="border border-charcoal/10 rounded-xl p-4" data-testid={`nominee-email-${d.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="font-semibold text-sm">{d.nominee_name}</span>
              <span className="text-xs text-muted">{d.category_name}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div>
                <div className="label mb-1 text-xs">To (editable)</div>
                <input defaultValue={d.to_email || ''} placeholder="venue@example.com"
                  onBlur={(e) => { patch(d.id, 'to_email', e.target.value.trim()); d.to_email = e.target.value.trim(); }}
                  data-testid={`nominee-email-to-${d.id}`}
                  className={`w-full bg-admin border rounded-xl px-3 py-2 text-sm ${d.to_email ? 'border-charcoal/15' : 'border-heritage/60'}`}/>
                {!d.to_email && <div className="text-[11px] text-heritage mt-1">No email on file for this venue — add one before sending.</div>}
              </div>
              <div>
                <div className="label mb-1 text-xs">Subject (editable)</div>
                <input defaultValue={d.subject} onBlur={(e) => patch(d.id, 'subject', e.target.value)}
                  data-testid={`nominee-email-subject-${d.id}`}
                  className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"/>
              </div>
            </div>
            <div className="label mb-1 text-xs">Message (editable)</div>
            <textarea defaultValue={d.body} rows={5} onBlur={(e) => patch(d.id, 'body', e.target.value)}
              data-testid={`nominee-email-body-${d.id}`}
              className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm leading-relaxed mb-3"/>
            <div className="flex items-center gap-3">
              <button onClick={() => send(d)} disabled={busy === d.id}
                data-testid={`nominee-email-send-${d.id}`}
                className="bg-forest text-[#FAF8F5] text-xs font-semibold px-4 py-2 rounded-full hover:opacity-90 disabled:opacity-50">
                {busy === d.id ? 'Sending…' : 'Approve & send'}
              </button>
              <button onClick={() => dismiss(d.id)} disabled={busy === d.id}
                data-testid={`nominee-email-dismiss-${d.id}`}
                className="text-xs text-muted hover:text-heritage font-semibold">
                Dismiss (don't send)
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAwards() {
  const [cycles, setCycles] = useState([]);
  const [entryDeadline, setEntryDeadline] = useState('');
  const [attractionsDeadline, setAttractionsDeadline] = useState('');
  useEffect(() => {
    api.get('/site-settings/awards_deadline').then((r) => {
      setEntryDeadline(r.data.value?.date || '');
      setAttractionsDeadline(r.data.value?.attractions_date || '');
    }).catch(() => {});
  }, []);
  async function saveDeadlines(date, attractionsDate) {
    setEntryDeadline(date);
    setAttractionsDeadline(attractionsDate);
    const value = {};
    if (date) value.date = date;
    if (attractionsDate) value.attractions_date = attractionsDate;
    try { await api.put('/admin/site-settings/awards_deadline', { value }); } catch { /* ignore */ }
  }
  const [activeCycle, setActiveCycle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [nominees, setNominees] = useState([]);
  const [nomineeFilter, setNomineeFilter] = useState('pending');
  const [audit, setAudit] = useState(null); // {loading, data, name}
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  function loadCycles() {
    api.get('/admin/awards/cycles').then((r) => {
      setCycles(r.data.items);
      if (!activeCycle && r.data.items[0]) setActiveCycle(r.data.items[0]);
    }).catch(() => {});
  }
  useEffect(loadCycles, []);

  useEffect(() => {
    if (!activeCycle) return;
    api.get(`/admin/awards/cycles/${activeCycle.id}/categories`).then((r) => setCategories(r.data.items)).catch(() => {});
    loadNominees();
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCycle, nomineeFilter]);

  function loadNominees() {
    if (!activeCycle) return;
    const q = nomineeFilter !== 'all' ? `&status=${nomineeFilter}` : '';
    api.get(`/admin/awards/nominees?cycle_id=${activeCycle.id}${q}`).then((r) => setNominees(r.data.items)).catch(() => {});
  }

  function loadCounts() {
    if (!activeCycle) return;
    api.get(`/admin/awards/nominees/counts?cycle_id=${activeCycle.id}`).then((r) => {
      setStatusCounts(r.data);
      // Empty Pending tab confuses admins — jump to Approved if that's where the nominees are
      setNomineeFilter((cur) => (cur === 'pending' && !r.data.pending && r.data.approved ? 'approved' : cur));
    }).catch(() => {});
  }

  // Cycle creation modal
  const [showNewCycle, setShowNewCycle] = useState(false);
  const [newCycle, setNewCycle] = useState({ year: new Date().getFullYear(), name: '', description: '', status: 'draft' });
  async function createCycle() {
    if (!newCycle.name) { alert('Name required'); return; }
    try {
      await api.post('/admin/awards/cycles', newCycle);
      setShowNewCycle(false);
      setNewCycle({ year: new Date().getFullYear(), name: '', description: '', status: 'draft' });
      loadCycles();
    } catch (e) { alert(e.response?.data?.detail || 'Create failed'); }
  }

  async function updateCycle(patch) {
    const r = await api.patch(`/admin/awards/cycles/${activeCycle.id}`, patch);
    setActiveCycle(r.data);
    loadCycles();
  }
  async function deleteCycle() {
    if (!window.confirm(`Delete ${activeCycle.name}? This removes all categories, nominees and votes for ${activeCycle.year}.`)) return;
    await api.delete(`/admin/awards/cycles/${activeCycle.id}`);
    setActiveCycle(null); loadCycles();
  }
  async function seedCategories() {
    try {
      await api.post(`/admin/awards/cycles/${activeCycle.id}/seed-categories`);
      const r = await api.get(`/admin/awards/cycles/${activeCycle.id}/categories`);
      setCategories(r.data.items);
    } catch (e) { alert(e.response?.data?.detail || 'Seed failed'); }
  }

  // Category management
  const [newCat, setNewCat] = useState('');
  async function addCategory() {
    if (!newCat) return;
    await api.post(`/admin/awards/cycles/${activeCycle.id}/categories`, { name: newCat, order: categories.length });
    setNewCat('');
    const r = await api.get(`/admin/awards/cycles/${activeCycle.id}/categories`);
    setCategories(r.data.items);
  }
  async function deleteCategory(id) {
    if (!window.confirm('Delete this category and all its nominees?')) return;
    await api.delete(`/admin/awards/categories/${id}`);
    const r = await api.get(`/admin/awards/cycles/${activeCycle.id}/categories`);
    setCategories(r.data.items);
    loadNominees();
  }
  async function updateCategory(id, patch) {
    await api.patch(`/admin/awards/categories/${id}`, patch);
    const r = await api.get(`/admin/awards/cycles/${activeCycle.id}/categories`);
    setCategories(r.data.items);
  }

  // Nominee management
  async function uploadNomineePhoto(id, file) {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/admin/awards/nominees/${id}/photo`, fd);
      loadNominees();
    } catch (e) {
      alert(e.response?.data?.detail || 'Photo upload failed');
    }
  }

  async function setNomineePhotoUrl(id) {
    const url = window.prompt('Paste an image URL for this nominee:');
    if (!url) return;
    try {
      await api.patch(`/admin/awards/nominees/${id}`, { hero_image: url });
      loadNominees();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  }

  async function openAudit(n) {
    setAudit({ loading: true, name: n.name, data: null });
    try {
      const r = await api.get(`/admin/awards/nominees/${n.id}/vote-audit`);
      setAudit({ loading: false, name: n.name, data: r.data });
    } catch {
      alert('Could not load the vote audit — try again');
      setAudit(null);
    }
  }

  async function refetchNomineePhoto(id) {
    try {
      await api.post(`/admin/awards/nominees/${id}/photo/refetch`);
      loadNominees();
    } catch (e) {
      alert(e.response?.data?.detail || 'Re-fetch failed');
    }
  }

  async function setNomineeStatus(id, status) {
    await api.patch(`/admin/awards/nominees/${id}`, { status });
    loadNominees();
    loadCounts();
  }
  async function editNomineeField(n, field, label) {
    const cur = n[field] || '';
    const v = window.prompt(`Edit ${label} for this nominee:`, cur);
    if (v === null || v === cur) return;
    try {
      await api.patch(`/admin/awards/nominees/${n.id}`, { [field]: v });
      loadNominees();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  }

  async function queueNomineeEmail(n) {
    try {
      const r = await api.post(`/admin/awards/nominees/${n.id}/queue-email`);
      if (r.data.already === 'sent') alert(`${n.name} has already been emailed.`);
      else if (r.data.already === 'draft') alert(`${n.name} already has a draft waiting in the email queue above.`);
      else {
        alert(`Email draft queued for ${n.name} — review and send it in the "You've been nominated" panel above.`);
        window.dispatchEvent(new Event('nominee-email-queued'));
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not queue email');
    }
  }

  async function toggleWinner(n, field) {
    await api.patch(`/admin/awards/nominees/${n.id}`, { [field]: !n[field] });
    loadNominees();
  }
  async function publishWinners() {
    if (!window.confirm('Publish winners now? This will apply a trophy badge to linked attractions and mark the cycle as published.')) return;
    const r = await api.post(`/admin/awards/cycles/${activeCycle.id}/publish-winners`);
    alert(`Published. ${r.data.winners} winners, ${r.data.badges_applied} attraction badges applied.`);
    await updateCycle({ status: 'published' });
  }

  // --- Historical awards: scan a document, review the parsed winners, import. ---
  const fileInputRef = useRef(null);
  const [scanResult, setScanResult] = useState(null);  // {ok, kind, count, winners[]} or null
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [importing, setImporting] = useState(false);
  const [createStubs, setCreateStubs] = useState(true);

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanError(''); setScanResult(null); setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/awards/scan-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!r.data.ok) {
        setScanError(r.data.error || 'Could not parse this document.');
      } else if (r.data.count === 0) {
        setScanError(r.data.error || 'Document parsed but no award entries detected. Try a clearer scan or a CSV/XLSX with columns Name, Year, Category.');
      } else {
        // Default-include: matched at >=50% OR (no match but create_stubs is on,
        // so it'll become a draft attraction)
        setScanResult({
          ...r.data,
          winners: r.data.winners.map((w) => ({
            ...w,
            _include: ((w.match_confidence || 0) >= 0.5 && !!w.matched_slug) || !w.matched_slug,
          })),
        });
      }
    } catch (err) {
      setScanError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function updateScanRow(i, patch) {
    setScanResult((prev) => ({
      ...prev,
      winners: prev.winners.map((w, idx) => idx === i ? { ...w, ...patch } : w),
    }));
  }

  async function importScanned() {
    // Two batches: ones with a real matched slug (apply badges to existing
    // attractions) and ones without (only included when "create stubs" is on,
    // backend will create the draft attraction then attach the badge).
    const toImport = scanResult.winners.filter((w) => {
      if (!w._include || !w.year || !w.category) return false;
      if (w.matched_slug) return true;
      return createStubs;  // include unmatched only if we're creating stubs
    });
    if (toImport.length === 0) {
      alert('No rows ticked / matched. Tick the rows you want, and make sure each has a year + category.');
      return;
    }
    setImporting(true);
    try {
      const r = await api.post('/admin/awards/import-historic', { winners: toImport, create_stubs: createStubs });
      const stubMsg = r.data.stubs_created ? ` Created ${r.data.stubs_created} draft attractions.` : '';
      alert(`Imported ${r.data.applied} badges. Skipped ${r.data.skipped} (dupes or invalid).${stubMsg}`);
      setScanResult(null);
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  // Existing attractions list for fixing unresolved fuzzy matches
  const [attractionsAll, setAttractionsAll] = useState([]);
  useEffect(() => {
    if (scanResult && attractionsAll.length === 0) {
      api.get('/admin/attractions?limit=500').then((r) => setAttractionsAll(r.data.items || []));
    }
  }, [scanResult, attractionsAll.length]);

  const counts = {
    pending: statusCounts.pending,
    approved: statusCounts.approved,
    rejected: statusCounts.rejected,
    winners: nominees.filter((n) => n.is_winner).length,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label text-heritage mb-2 inline-flex items-center gap-2"><Trophy size={14}/> Tourism Awards</div>
          <h1 className="font-display text-4xl">Run the annual awards.</h1>
        </div>
        <button onClick={() => setShowNewCycle(true)} data-testid="awards-new-cycle" className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
          <Plus size={14}/> New cycle
        </button>
      </div>

      {/* Entry deadlines — shown to partners beside every category in the criteria browser */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-6" data-testid="awards-deadline-card">
        <div className="flex items-start gap-3 mb-4">
          <Clock size={16} className="text-heritage shrink-0 mt-0.5"/>
          <div>
            <div className="font-semibold text-sm">Entry deadlines</div>
            <div className="text-xs text-muted mt-0.5">Shown as a banner and beside every category in the criteria browser, with a days-left countdown. The Visitor Attraction categories (Large &amp; Small) close earlier than the rest — set their date separately. Clear a date to hide it.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="label text-charcoal/60 mb-1.5">Visitor Attraction categories (Large &amp; Small)</div>
            <div className="flex items-center gap-2">
              <input type="date" value={attractionsDeadline} onChange={(e) => saveDeadlines(entryDeadline, e.target.value)}
                data-testid="awards-attractions-deadline-input" className="bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"/>
              {attractionsDeadline && <button onClick={() => saveDeadlines(entryDeadline, '')} data-testid="awards-attractions-deadline-clear" className="text-xs font-semibold text-heritage hover:underline">Clear</button>}
            </div>
          </div>
          <div>
            <div className="label text-charcoal/60 mb-1.5">All other categories</div>
            <div className="flex items-center gap-2">
              <input type="date" value={entryDeadline} onChange={(e) => saveDeadlines(e.target.value, attractionsDeadline)}
                data-testid="awards-deadline-input" className="bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"/>
              {entryDeadline && <button onClick={() => saveDeadlines('', attractionsDeadline)} data-testid="awards-deadline-clear" className="text-xs font-semibold text-heritage hover:underline">Clear</button>}
            </div>
          </div>
        </div>
      </div>

      <HelpCallout
        title="Annual Tourism Awards — your big yearly traffic moment."
        description="Run a cycle in 3 phases: open Nominations (public submits), close + Approve to curate the ballot, open Voting (public votes), then close + Publish Winners. Every public submission lands in the Pending tab below — nothing reaches the public ballot until YOU approve it. The Overview dashboard and the Awards sidebar item both show a red badge whenever new nominations are waiting. Publishing a cycle stamps a 🏆 trophy badge on every winner's attraction page automatically."
        appearsAt={[
          { label: '/awards public page', where: 'state-aware: nomination forms → ballot (approved only) → winners' },
          { label: '/awards/<year>', where: 'permanent showcase of past cycle winners' },
          { label: 'Attraction card + detail trophy badges', where: 'auto-applied when you publish winners' },
          { label: 'Friday newsletter 🏆 leading nominee', where: 'highest-voted approved nominee from past 7 days' },
        ]}
      />

      {/* Import historic / pre-platform awards from a document */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10" data-testid="awards-import-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div>
            <div className="label text-mustard mb-2 inline-flex items-center gap-2"><FileSearch size={14}/> Historic awards import</div>
            <h2 className="font-display text-2xl">Add past winners from a document.</h2>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.tsv,.txt,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg,.webp"
            onChange={onScanFile}
            data-testid="awards-import-file"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning}
            data-testid="awards-import-trigger"
            className="bg-charcoal text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-heritage disabled:opacity-60"
          >
            <Upload size={14}/> {scanning ? 'Scanning…' : 'Upload document'}
          </button>
        </div>
        <p className="text-sm text-muted">
          Drop a PDF, Word document (DOCX), image (poster / certificate scan), or spreadsheet (CSV / XLSX) listing past tourism-award winners.
          We&apos;ll extract names, years and categories, fuzzy-match them to your attractions, and let you review before applying badges.
        </p>
        {scanError && (
          <div className="mt-4 bg-heritage/10 border border-heritage/40 rounded-xl p-3 text-sm text-heritage" data-testid="awards-import-error">
            {scanError}
          </div>
        )}
        {scanResult?.ok && scanResult.count > 0 && (
          <div className="mt-5" data-testid="awards-import-results">
            {(() => {
              const ticked = scanResult.winners.filter((w) => w._include);
              const tickedMatched = ticked.filter((w) => w.matched_slug);
              const tickedUnmatched = ticked.length - tickedMatched.length;
              return (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="text-sm">
                      <span className="font-semibold text-forest">Found {scanResult.count} entries</span>
                      <span className="text-muted"> · format: {scanResult.kind}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="inline-flex items-center gap-2 text-xs text-charcoal/80 select-none cursor-pointer" data-testid="awards-import-create-stubs-label">
                        <input
                          type="checkbox"
                          checked={createStubs}
                          onChange={(e) => setCreateStubs(e.target.checked)}
                          data-testid="awards-import-create-stubs"
                        />
                        Create draft attractions for unmatched venues
                      </label>
                      <button
                        onClick={importScanned}
                        disabled={importing}
                        data-testid="awards-import-apply"
                        className="bg-forest text-base px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep disabled:opacity-60"
                      >
                        <Check size={14}/>
                        {importing ? 'Importing…' : `Import ${ticked.length} ticked`}
                      </button>
                    </div>
                  </div>
                  {createStubs && tickedUnmatched > 0 && (
                    <div className="bg-mustard/15 border border-mustard/40 rounded-xl px-3 py-2 mb-3 text-xs text-charcoal/85" data-testid="awards-import-stub-warning">
                      ↳ {tickedUnmatched} new draft attraction{tickedUnmatched === 1 ? '' : 's'} will be created (published=false). Find them later under <span className="font-mono">/admin/attractions?filter=unpublished</span> to add photos, postcodes and opening days.
                    </div>
                  )}
            <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {scanResult.winners.map((w, i) => {
                const willCreateStub = !w.matched_slug && w._include && createStubs;
                const ok = (!!w.matched_slug && w._include) || willCreateStub;
                return (
                  <div
                    key={i}
                    data-testid={`awards-import-row-${i}`}
                    className={`grid grid-cols-1 md:grid-cols-[2rem,1fr,7rem,1fr,9rem] gap-3 items-start bg-admin border ${ok ? 'border-forest/30' : 'border-charcoal/10'} rounded-xl p-3 text-sm`}
                  >
                    <input
                      type="checkbox"
                      checked={!!w._include}
                      onChange={(e) => updateScanRow(i, { _include: e.target.checked })}
                      className="mt-1"
                      data-testid={`awards-import-row-include-${i}`}
                    />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{w.attraction_name}</div>
                      <select
                        value={w.matched_slug || ''}
                        onChange={(e) => updateScanRow(i, { matched_slug: e.target.value, match_confidence: 1.0 })}
                        className="mt-1 w-full bg-white border border-charcoal/15 rounded-lg px-2 py-1 text-xs"
                        data-testid={`awards-import-row-slug-${i}`}
                      >
                        <option value="">— No match —</option>
                        {attractionsAll.map((a) => (
                          <option key={a.slug} value={a.slug}>{a.name}{a.town ? ` · ${a.town}` : ''}</option>
                        ))}
                      </select>
                      <div className="text-[10px] text-muted mt-1">
                        {willCreateStub
                          ? <span className="text-mustard font-semibold">↳ Will create draft attraction</span>
                          : <>Match confidence: {Math.round((w.match_confidence || 0) * 100)}%</>}
                      </div>
                    </div>
                    <input
                      type="number"
                      min="1900"
                      max="2100"
                      value={w.year || ''}
                      onChange={(e) => updateScanRow(i, { year: e.target.value ? parseInt(e.target.value, 10) : null })}
                      placeholder="Year"
                      className="bg-white border border-charcoal/15 rounded-lg px-2 py-1"
                      data-testid={`awards-import-row-year-${i}`}
                    />
                    <input
                      value={w.category || ''}
                      onChange={(e) => updateScanRow(i, { category: e.target.value })}
                      placeholder="Award / category"
                      className="bg-white border border-charcoal/15 rounded-lg px-2 py-1"
                      data-testid={`awards-import-row-category-${i}`}
                    />
                    <div className="flex items-center justify-end">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-stone text-charcoal/70 font-semibold">Historic</span>
                    </div>
                  </div>
                );
              })}
            </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Cycle tabs */}
      {cycles.length > 0 && (
        <div className="flex gap-2 mb-8 flex-wrap">
          {cycles.map((c) => (
            <button key={c.id} onClick={() => setActiveCycle(c)} data-testid={`cycle-tab-${c.year}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold ${activeCycle?.id === c.id ? 'bg-charcoal text-base' : 'bg-white border border-charcoal/15'}`}>
              {c.year} · <span className={`ml-1 inline-block w-2 h-2 rounded-full ${STATUS_COLOUR[c.status]?.replace('text-base','').replace('text-charcoal','') || 'bg-muted'}`}/>
            </button>
          ))}
        </div>
      )}

      {cycles.length === 0 && (
        <div className="text-center py-20 text-muted bg-white border border-charcoal/10 rounded-3xl">
          <Award size={48} className="mx-auto mb-4 text-mustard"/>
          <div className="font-display text-2xl mb-2">No award cycles yet</div>
          <p>Click &quot;New cycle&quot; to start a year of awards.</p>
        </div>
      )}

      {activeCycle && (
        <>
          {/* Cycle status row */}
          <div className="bg-white border border-charcoal/10 rounded-3xl p-6 mb-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-5">
                <div className="font-display text-2xl">{activeCycle.name}</div>
                <div className="text-muted text-sm mt-1">{activeCycle.description || 'No description'}</div>
              </div>
              <div className="md:col-span-4">
                <div className="label text-charcoal/50 mb-1.5">Status</div>
                <select value={activeCycle.status} onChange={(e) => updateCycle({ status: e.target.value })} data-testid="cycle-status-select"
                  className={`w-full px-4 py-2.5 rounded-xl font-semibold ${STATUS_COLOUR[activeCycle.status] || ''}`}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-2">
                <button onClick={publishWinners} data-testid="publish-winners" className="bg-mustard text-charcoal px-4 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Trophy size={12}/> Publish winners</button>
                <button onClick={deleteCycle} className="bg-admin border border-heritage/30 text-heritage px-3 py-2.5 rounded-full text-xs font-semibold"><Trash2 size={12}/></button>
              </div>
            </div>
            {/* Ballot intro + scheduled dates */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-5 border-t border-charcoal/10">
              <label className="block">
                <div className="label mb-1.5">Public ballot intro <span className="text-muted normal-case">(shown above the categories on /awards)</span></div>
                <textarea rows={3} defaultValue={activeCycle.description || ''} onBlur={(e) => e.target.value !== activeCycle.description && updateCycle({ description: e.target.value })} data-testid="cycle-description"
                  placeholder="One or two sentences that frame the awards for visitors. e.g. 'Vote for the venues, events and businesses that made your Leicestershire year.'"
                  className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 text-sm"/>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <div className="label mb-1.5">Nominations open</div>
                  <input type="date" defaultValue={(activeCycle.nomination_opens_at || '').slice(0,10)} onBlur={(e) => updateCycle({ nomination_opens_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="cycle-noms-open" className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm"/>
                </label>
                <label>
                  <div className="label mb-1.5">Nominations close</div>
                  <input type="date" defaultValue={(activeCycle.nomination_closes_at || '').slice(0,10)} onBlur={(e) => updateCycle({ nomination_closes_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="cycle-noms-close" className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm"/>
                </label>
                <label>
                  <div className="label mb-1.5">Voting opens</div>
                  <input type="date" defaultValue={(activeCycle.voting_opens_at || '').slice(0,10)} onBlur={(e) => updateCycle({ voting_opens_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="cycle-vote-open" className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm"/>
                </label>
                <label>
                  <div className="label mb-1.5">Voting closes</div>
                  <input type="date" defaultValue={(activeCycle.voting_closes_at || '').slice(0,10)} onBlur={(e) => updateCycle({ voting_closes_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="cycle-vote-close" className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm"/>
                </label>
              </div>
            </div>
            <div className="text-xs text-muted pt-2">Status auto-flips daily based on these dates — set them and the cycle moves itself through Open → Closed → Voting → Voting Closed.</div>
          </div>

          {/* Categories */}
          <div className="bg-white border border-charcoal/10 rounded-3xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">Categories ({categories.length})</h3>
              {categories.length === 0 && <button onClick={seedCategories} data-testid="seed-categories" className="bg-charcoal text-base px-4 py-2 rounded-full text-xs font-semibold">Seed 7 defaults</button>}
            </div>
            <div className="space-y-3 mb-4">
              {categories.map((c) => (
                <div key={c.id} className="bg-admin border border-charcoal/10 rounded-xl px-4 py-3" data-testid={`category-${c.slug}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold flex-1">{c.name}</div>
                    <button onClick={() => deleteCategory(c.id)} className="p-2 text-heritage hover:bg-heritage/5 rounded-lg"><Trash2 size={14}/></button>
                  </div>
                  <textarea
                    rows={2}
                    defaultValue={c.description || ''}
                    onBlur={(e) => e.target.value !== (c.description || '') && updateCategory(c.id, { description: e.target.value })}
                    placeholder={`Optional intro for the "${c.name}" category — shown above its nominees on the public ballot.`}
                    data-testid={`category-${c.slug}-desc`}
                    className="w-full mt-2 bg-white border border-charcoal/10 rounded-lg px-3 py-2 text-xs text-charcoal/85"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" data-testid="category-new-input" className="flex-1 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/>
              <button onClick={addCategory} data-testid="category-add" className="bg-forest text-base px-5 rounded-xl font-semibold"><Plus size={14}/></button>
            </div>
          </div>

          {/* Nomination emails awaiting approval */}
          <NomineeEmailQueue />

          {/* Nominees */}
          <div className="bg-white border border-charcoal/10 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-display text-2xl">Nominees</h3>
              <div className="flex gap-2 flex-wrap text-xs">
                <button onClick={() => setNomineeFilter('pending')} data-testid="nominee-filter-pending" className={`px-4 py-2 rounded-full font-semibold ${nomineeFilter === 'pending' ? 'bg-heritage text-base' : 'bg-admin border border-charcoal/15'}`}>Pending ({counts.pending})</button>
                <button onClick={() => setNomineeFilter('approved')} data-testid="nominee-filter-approved" className={`px-4 py-2 rounded-full font-semibold ${nomineeFilter === 'approved' ? 'bg-forest text-base' : 'bg-admin border border-charcoal/15'}`}>Approved ({counts.approved})</button>
                <button onClick={() => setNomineeFilter('rejected')} className={`px-4 py-2 rounded-full font-semibold ${nomineeFilter === 'rejected' ? 'bg-charcoal text-base' : 'bg-admin border border-charcoal/15'}`}>Rejected</button>
                <button onClick={() => setNomineeFilter('all')} className={`px-4 py-2 rounded-full font-semibold ${nomineeFilter === 'all' ? 'bg-charcoal text-base' : 'bg-admin border border-charcoal/15'}`}>All ({counts.pending + counts.approved + counts.rejected})</button>
              </div>
            </div>
            <div className="space-y-3">
              {nominees.map((n) => {
                const cat = categories.find((c) => c.id === n.category_id);
                return (
                  <div key={n.id} className="bg-admin border border-charcoal/10 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`nominee-row-${n.id}`}>
                    <div className="md:col-span-2">
                      {n.hero_image ? <img src={n.hero_image} alt="" className="w-full aspect-[4/3] object-cover rounded-xl bg-stone" onError={(e) => { e.currentTarget.style.opacity = 0.25; }}/> : <div className="aspect-[4/3] bg-stone rounded-xl flex items-center justify-center text-muted"><Trophy/></div>}
                      <div className="flex items-center justify-center gap-2 mt-1.5">
                        <label className="text-[11px] font-semibold text-forest cursor-pointer hover:underline" data-testid={`photo-upload-${n.id}`}>
                          Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadNomineePhoto(n.id, e.target.files[0]); e.target.value = ''; }}/>
                        </label>
                        <button onClick={() => setNomineePhotoUrl(n.id)} data-testid={`photo-url-${n.id}`} className="text-[11px] font-semibold text-charcoal/60 hover:underline">URL</button>
                        {n.website && <button onClick={() => refetchNomineePhoto(n.id)} data-testid={`photo-refetch-${n.id}`} className="text-[11px] font-semibold text-charcoal/60 hover:underline">Re-fetch</button>}
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-display text-lg">{n.name}</span>
                        {n.image_needs_review && <span data-testid={`photo-review-${n.id}`} className="bg-heritage/10 text-heritage text-xs font-bold px-2 py-0.5 rounded-full" title={n.image_source_url ? `Sourced from ${n.image_source_url}` : ''}>📷 Photo from their website — check before approving</span>}
                        {n.image_source === 'media_library' && <span className="bg-forest/10 text-forest text-xs font-semibold px-2 py-0.5 rounded-full">📷 Photo from media library</span>}
                        {n.is_winner && <span className="bg-mustard text-charcoal text-xs font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Trophy size={10}/> Winner</span>}
                        {n.is_runner_up && <span className="bg-stone text-charcoal text-xs font-bold px-2 py-0.5 rounded-full">Runner-up</span>}
                      </div>
                      <div className="text-xs text-muted mb-1">{cat?.name || '—'} · {n.town || '—'} · <strong>{n.votes_count || 0}</strong> votes
                        {(n.votes_count || 0) > 0 && (
                          <button onClick={() => openAudit(n)} data-testid={`vote-audit-${n.id}`}
                            className="ml-2 text-[11px] font-semibold text-heritage hover:underline">Audit votes</button>
                        )}
                      </div>
                      {n.blurb && <div className="text-sm text-charcoal/85 line-clamp-2">{n.blurb}</div>}
                      <div className="flex gap-3 mt-1">
                        <button onClick={() => editNomineeField(n, 'name', 'the venue name')} data-testid={`edit-nominee-name-${n.id}`} className="text-[11px] font-semibold text-charcoal/60 hover:text-heritage hover:underline">Edit name</button>
                        <button onClick={() => editNomineeField(n, 'blurb', 'the description')} data-testid={`edit-nominee-blurb-${n.id}`} className="text-[11px] font-semibold text-charcoal/60 hover:text-heritage hover:underline">Edit description</button>
                        <button onClick={() => editNomineeField(n, 'town', 'the town')} data-testid={`edit-nominee-town-${n.id}`} className="text-[11px] font-semibold text-charcoal/60 hover:text-heritage hover:underline">Edit town</button>
                      </div>
                      {n.submitted_by_name && <div className="text-xs text-muted mt-1">Submitted by {n.submitted_by_name}</div>}
                    </div>
                    <div className="md:col-span-4 flex flex-wrap gap-2 justify-end">
                      {n.status !== 'approved' && <button onClick={() => setNomineeStatus(n.id, 'approved')} data-testid={`approve-${n.id}`} className="bg-forest text-base px-3 py-1.5 rounded-full text-xs font-semibold"><Check size={12}/></button>}
                      {n.status !== 'rejected' && <button onClick={() => setNomineeStatus(n.id, 'rejected')} className="border border-charcoal/15 px-3 py-1.5 rounded-full text-xs font-semibold"><X size={12}/></button>}
                      {n.status === 'approved' && (
                        <>
                          <button onClick={() => queueNomineeEmail(n)} data-testid={`queue-email-${n.id}`} className="border border-forest/40 text-forest px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-forest/5">✉ Queue email</button>
                          <button onClick={() => toggleWinner(n, 'is_winner')} data-testid={`winner-${n.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${n.is_winner ? 'bg-mustard text-charcoal' : 'border border-charcoal/15'}`}>🏆 Winner</button>
                          <button onClick={() => toggleWinner(n, 'is_runner_up')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${n.is_runner_up ? 'bg-stone text-charcoal' : 'border border-charcoal/15'}`}>Runner-up</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {nominees.length === 0 && <div className="text-center py-12 text-muted">No nominees in this filter.</div>}
            </div>
          </div>
        </>
      )}

      {/* Vote audit modal */}
      {audit && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAudit(null)} data-testid="vote-audit-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-display text-2xl">Vote audit — {audit.name}</h3>
              <button onClick={() => setAudit(null)} data-testid="vote-audit-close" className="px-3 py-1 rounded-lg border border-charcoal/15 text-sm">Close</button>
            </div>
            {audit.loading && <div className="py-10 text-center text-muted">Analysing votes…</div>}
            {audit.data && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="bg-admin rounded-xl p-3 text-center"><div className="font-display text-2xl">{audit.data.public_votes}</div><div className="text-[11px] uppercase tracking-wide text-charcoal/60">Public votes</div></div>
                  <div className="bg-admin rounded-xl p-3 text-center"><div className="font-display text-2xl">{audit.data.manual_adjustments}</div><div className="text-[11px] uppercase tracking-wide text-charcoal/60">Admin added</div></div>
                  <div className="bg-admin rounded-xl p-3 text-center"><div className="font-display text-2xl">{audit.data.distinct_connections}</div><div className="text-[11px] uppercase tracking-wide text-charcoal/60">Different connections</div></div>
                  <div className="bg-admin rounded-xl p-3 text-center"><div className="font-display text-2xl">{audit.data.max_votes_in_60min}</div><div className="text-[11px] uppercase tracking-wide text-charcoal/60">Busiest hour</div></div>
                </div>
                <ul className="space-y-2 mb-5" data-testid="vote-audit-notes">
                  {audit.data.notes.map((note, i) => (
                    <li key={i} className={`text-sm rounded-xl px-3 py-2 ${note.startsWith('⚠') ? 'bg-heritage/10 text-heritage font-medium' : 'bg-stone/60 text-charcoal/80'}`}>{note}</li>
                  ))}
                </ul>
                {audit.data.by_day.length > 0 && (
                  <div>
                    <div className="label mb-2">Votes per day</div>
                    <div className="space-y-1">
                      {audit.data.by_day.map((d) => {
                        const max = Math.max(...audit.data.by_day.map((x) => x.count));
                        return (
                          <div key={d.date} className="flex items-center gap-2 text-xs">
                            <span className="w-24 shrink-0 text-charcoal/60">{d.date}</span>
                            <div className="flex-1 bg-stone/50 rounded-full h-4 overflow-hidden"><div className="bg-heritage h-4 rounded-full" style={{ width: `${Math.max(4, (d.count / max) * 100)}%` }}/></div>
                            <span className="w-8 text-right font-semibold">{d.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-charcoal/50 mt-4">Votes are anonymous — we store a one-way fingerprint of each connection, never the address itself. First vote: {audit.data.first_vote ? new Date(audit.data.first_vote).toLocaleString('en-GB') : '—'} · Last: {audit.data.last_vote ? new Date(audit.data.last_vote).toLocaleString('en-GB') : '—'}</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* New cycle modal */}
      {showNewCycle && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8">
            <h3 className="font-display text-2xl mb-6">New award cycle</h3>
            <label className="block mb-3"><div className="label mb-1.5">Year</div><input type="number" value={newCycle.year} onChange={(e) => setNewCycle({...newCycle, year: parseInt(e.target.value)})} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="new-cycle-year"/></label>
            <label className="block mb-3"><div className="label mb-1.5">Name</div><input value={newCycle.name} onChange={(e) => setNewCycle({...newCycle, name: e.target.value})} placeholder={`Leicestershire Tourism Awards ${newCycle.year}`} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="new-cycle-name"/></label>
            <label className="block mb-6"><div className="label mb-1.5">Description</div><textarea rows={3} value={newCycle.description} onChange={(e) => setNewCycle({...newCycle, description: e.target.value})} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowNewCycle(false)} className="px-5 py-2.5 rounded-full font-semibold border border-charcoal/15">Cancel</button>
              <button onClick={createCycle} data-testid="new-cycle-submit" className="bg-forest text-base px-6 py-2.5 rounded-full font-semibold">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
